import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import themeable from '@instructure/ui-themeable';
import { PLAYING } from '../../constants';
import MediaContext from '../Player/MediaContext';
var styles = {
  componentId: 'bdGGm',
  template: function template(theme) {
    return "\n\n.bdGGm_crZr{position:absolute;top:0}\n\n.bdGGm_cJVF{align-items:center;background-color:".concat(theme.backgroundColor || 'inherit', ";border:").concat(theme.borderWidth || 'inherit', " solid ").concat(theme.borderColor || 'inherit', ";border-radius:100%;color:").concat(theme.borderColor || 'inherit', ";display:flex;height:").concat(theme.radius || 'inherit', ";justify-content:center;left:calc(").concat(theme.radius || 'inherit', "/-2);outline:0;padding:0;pointer-events:auto;position:relative;top:calc(").concat(theme.radius || 'inherit', "/-2);transition-duration:0.2s;transition-property:width,height,top,left;width:").concat(theme.radius || 'inherit', "}\n\n.bdGGm_cJVF:focus{box-shadow:0 0 1px 4px ").concat(theme.shadowColor || 'inherit', ";outline:none}\n\n.bdGGm_cJVF:focus:before{border:0.125rem solid ").concat(theme.focusBorderColor || 'inherit', ";border-radius:100%;bottom:-0.3125rem;box-sizing:border-box;content:\"\";left:-0.3125rem;pointer-events:none;position:absolute;right:-0.3125rem;top:-0.3125rem}\n\n.bdGGm_dHNk{height:calc(").concat(theme.radius || 'inherit', "*2);left:calc(").concat(theme.radius || 'inherit', "*-1);top:calc(").concat(theme.radius || 'inherit', "*-1);transition-duration:0.2s;transition-property:width,height,top,left;width:calc(").concat(theme.radius || 'inherit', "*2)}\n\n.bdGGm_dnnz{display:flex;opacity:1;transition-duration:0.4s;transition-property:opacity}\n\n.bdGGm_dHNk .bdGGm_dnnz,.bdGGm_ehxe{display:none;opacity:0}\n\n.bdGGm_dHNk .bdGGm_ehxe{display:flex;opacity:1;transition-duration:0.4s;transition-property:opacity}");
  },
  'container': 'bdGGm_crZr',
  'circle': 'bdGGm_cJVF',
  'circleHovered': 'bdGGm_dHNk',
  'icon': 'bdGGm_dnnz',
  'hoverIcon': 'bdGGm_ehxe'
};
import theme from './theme';
/**
---
private: true
---
@module PlayerMarker
**/

var PlayerMarker = (_dec = themeable(theme, styles), _dec(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(PlayerMarker, _Component);

  function PlayerMarker() {
    var _getPrototypeOf2;

    var _this;

    _classCallCheck(this, PlayerMarker);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _possibleConstructorReturn(this, (_getPrototypeOf2 = _getPrototypeOf(PlayerMarker)).call.apply(_getPrototypeOf2, [this].concat(args)));
    _this.state = {
      isFocused: false,
      isHovered: false
    };

    _this.focusMarker = function (id) {
      var instanceRef = PlayerMarker._instances[id];

      if (instanceRef) {
        instanceRef.buttonRef.focus(); // This is a workaround for an issue caused by the inert polyfill where the polyfill
        // uses a MutationObserver to detect the inert attribute change, and MutationObserver
        // events are asynchronous, so we have to defer focus() to the next tick.
        // https://github.com/WICG/inert/blob/v3.0.0/README.md

        setTimeout(function () {
          instanceRef.buttonRef.focus();
        });
      }
    };

    _this.handleClick = function (e) {
      e.stopPropagation();
      var onClick = _this.props.onClick;
      onClick && onClick(e);
    };

    _this.handleOnMouseMove = function (event) {
      event.stopPropagation();

      _this.setHovered(true);
    };

    return _this;
  }

  _createClass(PlayerMarker, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      PlayerMarker._instances[this.props.id] = this;
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      delete PlayerMarker._instances[this.props.id];
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      var _this$props = this.props,
          currentTime = _this$props.currentTime,
          id = _this$props.id,
          mediaState = _this$props.mediaState,
          onReached = _this$props.onReached,
          time = _this$props.time,
          seeking = _this$props.seeking;
      var prevTime = prevProps.currentTime,
          prevId = prevProps.id;

      if (id !== prevId) {
        PlayerMarker._instances[id] = this;
        delete PlayerMarker._instances[prevId];
      }

      if (seeking || !onReached || mediaState !== PLAYING) {
        return;
      }

      var markerPassedByPlayhead = time > prevTime && time <= currentTime;
      var markerAtZeroAndPlaying = time === 0 && prevTime === 0 && currentTime > 0;

      if (markerPassedByPlayhead || markerAtZeroAndPlaying) {
        onReached();
      }
    }
  }, {
    key: "setFocused",
    value: function setFocused(isFocused) {
      this.setState({
        isFocused: isFocused
      });
    }
  }, {
    key: "setHovered",
    value: function setHovered(isHovered) {
      this.setState({
        isHovered: isHovered
      });
    }
  }, {
    key: "renderCircle",
    value: function renderCircle() {
      var _circleClasses,
          _this2 = this;

      var _this$props2 = this.props,
          active = _this$props2.active,
          hoverIcon = _this$props2.hoverIcon,
          icon = _this$props2.icon,
          label = _this$props2.label,
          onClick = _this$props2.onClick;
      var _this$state = this.state,
          isFocused = _this$state.isFocused,
          isHovered = _this$state.isHovered;
      var Component = onClick ? 'button' : 'div';
      var circleClasses = (_circleClasses = {}, _defineProperty(_circleClasses, styles.circle, true), _defineProperty(_circleClasses, styles.circleHovered, onClick && (active || isFocused || isHovered)), _circleClasses);
      var clickableProps = onClick ? {
        onBlur: function onBlur() {
          _this2.setFocused(false);
        },
        onClick: this.handleClick,
        onFocus: function onFocus() {
          _this2.setFocused(true);
        },
        onMouseMove: this.handleOnMouseMove,
        onMouseOut: function onMouseOut() {
          _this2.setHovered(false);
        },
        onMouseOver: function onMouseOver() {
          _this2.setHovered(true);
        },
        ref: function ref(el) {
          _this2.buttonRef = el;
        }
      } : null;
      /* eslint-disable jsx-a11y/mouse-events-have-key-events */

      return React.createElement(Component, Object.assign({
        className: classNames(circleClasses)
      }, clickableProps), hoverIcon && React.createElement("span", {
        className: styles.hoverIcon
      }, hoverIcon), icon && React.createElement("span", {
        className: styles.icon
      }, icon), React.createElement(ScreenReaderContent, null, label));
      /* eslint-enable jsx-a11y/mouse-events-have-key-events */
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var _this$props3 = this.props,
          duration = _this$props3.duration,
          time = _this$props3.time,
          variant = _this$props3.variant;

      if (variant === 'hidden') {
        return null;
      }

      var position = time / duration * 100 || 0;
      return React.createElement(MediaContext.Consumer, null, function (_ref) {
        var setActions = _ref.setActions;
        setActions({
          focusMarker: _this3.focusMarker
        });
        return React.createElement("div", {
          className: styles.container,
          style: {
            left: "".concat(position, "%")
          }
        }, variant === 'circle' ? _this3.renderCircle() : null);
      });
    }
  }]);

  PlayerMarker.displayName = "PlayerMarker";
  return PlayerMarker;
}(Component), _class2.propTypes = {
  active: PropTypes.bool,
  currentTime: PropTypes.number,
  duration: PropTypes.number,
  hoverIcon: PropTypes.node,
  icon: PropTypes.node,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  mediaState: PropTypes.string,
  onClick: PropTypes.func,
  onReached: PropTypes.func,
  time: PropTypes.number,
  seeking: PropTypes.bool,
  variant: PropTypes.oneOf(['circle', 'hidden'])
}, _class2.defaultProps = {
  active: false,
  currentTime: 0,
  duration: 0,
  hoverIcon: null,
  icon: null,
  label: '',
  mediaState: null,
  onClick: null,
  onReached: null,
  time: 0,
  seeking: false,
  variant: 'hidden'
}, _class2._instances = {}, _temp)) || _class);
export default PlayerMarker;